import type { ComponentType, ReactNode } from 'react';

/**
 * Icon style variants, matching iconsax-react. The sidebar renders the active
 * item as `Bold` and the rest as `Linear`, which is how the legacy MUI apps
 * signalled selection.
 */
export type IconVariant = 'Linear' | 'Outline' | 'Broken' | 'Bold' | 'Bulk' | 'TwoTone';

/**
 * Any icon the design system will render. Deliberately structural rather than
 * `import type { Icon } from 'iconsax-react'` so @radiology/ui does not force
 * one icon library on consumers.
 */
export type IconComponent = ComponentType<{
  size?: string | number;
  color?: string;
  variant?: IconVariant;
}>;

/** The four business modules. Matches the database schema names exactly. */
export type ModuleKey = 'certifications' | 'purchasing' | 'attendance' | 'devices';

export const MODULE_KEYS: readonly ModuleKey[] = [
  'certifications',
  'purchasing',
  'attendance',
  'devices',
] as const;

/**
 * Canonical role vocabulary.
 *
 * Keycloak stores per-app client roles with inconsistent casing — `Employee` in
 * cert-mng but `employee` in purchase-tracking, `chief` for what is really a
 * chief approver. Those raw values are mapped onto this list at the edge so no
 * component ever branches on a Keycloak-specific spelling.
 */
export type Role =
  | 'admin'
  // Certifications only, from the "Manager Role Enhancement & Rename" CR:
  // unrestricted, and what every pre-CR `manager` grant became (AC-01).
  | 'operation-manager'
  | 'manager'
  | 'supervisor'
  | 'approver'
  | 'chief-approver'
  | 'employee'
  | 'secretary'
  | 'management';

export interface NavItem {
  /** Stable id, unique within its module. Used for active-state matching. */
  key: string;
  label: string;
  /** Absolute path, e.g. /certifications/manager/dashboard */
  to: string;
  icon?: IconComponent;
  /** Optional count shown as a badge, e.g. pending approvals. */
  badge?: number;
}

export interface NavGroup {
  key: string;
  /** Omit for an ungrouped list; shown as a small uppercase caption when present. */
  label?: string;
  items: NavItem[];
}

export interface AppModule {
  key: ModuleKey;
  label: string;
  description?: string;
  icon: IconComponent;
  /** Where the app switcher sends the user for this module. */
  to: string;
  /** False when the signed-in user holds no role in this module. */
  available: boolean;
}

/** The hospital / section / role selection carried across the session. */
export interface UserContext {
  role?: Role;
  hospital?: string;
  section?: string;
}

export interface CurrentUser {
  username: string;
  displayName?: string;
  email?: string;
  avatarUrl?: string;
}

export interface Breadcrumb {
  label: string;
  to?: string;
}

export interface AppShellProps {
  modules: AppModule[];
  activeModule: ModuleKey;
  navGroups: NavGroup[];
  user?: CurrentUser;
  userContext?: UserContext;
  /** Roles the user holds in the active module, for the role switcher. */
  availableRoles?: Role[];
  breadcrumbs?: Breadcrumb[];
  pageTitle?: string;
  pageActions?: ReactNode;
  onNavigate: (to: string) => void;
  onSignOut?: () => void;
  onChangeRole?: (role: Role) => void;
  onOpenContextPicker?: () => void;
  children: ReactNode;
}
